/**
 * A LISTA DOS CAMPOS, e o que se pode fazer com um deles.
 *
 * É a tela que quem hospeda abre no dia a dia. A pergunta que ela responde primeiro é "este Campo
 * está vivo?", e por isso a linha de cada um começa pelos notebooks e pelo último envio: Campo com
 * notebook inscrito e silêncio de uma semana é o que precisa de telefonema.
 *
 * ## Por que o treinamento é SUB-LINHA, e não uma linha da lista
 *
 * Cada Campo tem no máximo um gêmeo, e uma lista que mostrasse o dobro de Campos sem explicação
 * faria quem abre contar errado. Como sub-linha, ele aparece onde pertence: embaixo do Campo dele,
 * recuado, com a geração e os notebooks de treinamento.
 *
 * ## Uma coisa por vez
 *
 * Só um formulário fica aberto, e isso é escolhido: as quatro ações pedem motivo escrito, e duas
 * delas mexem em acesso. Dois formulários abertos ao mesmo tempo, num celular, é o caminho para
 * alguém digitar o motivo de um e apertar o botão do outro.
 */
import { useCallback, useEffect, useState, type FormEvent, type JSX } from 'react'
import { ehSessaoTerminada, ErroDoServidor, servidor } from '../../api'
import {
  administracao, type CampoNaLista, type CodigoDoNotebook, type CongregacaoDoCampo,
  type EuDaAdministracao
} from '../../api-da-administracao'
import { quandoFoi } from '../../dinheiro'
import { BarraDaAdministracao } from '../Administracao'
import { CampoNovo } from './CampoNovo'
import { OCadastro, type FocoDoCadastro } from './OCadastro'

/** `2026-08-05T12:00:00Z` vira `05/08/2026`. */
function dia(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso.slice(0, 10)
    : `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
}

/**
 * Uma contagem com a palavra no número certo.
 *
 * "1 contas de painel desativadas" é o tipo de frase que faz quem lê desconfiar de tudo o que
 * está na tela, inclusive dos números que estão certos.
 */
function contagem(quantos: number, singular: string, plural: string): string {
  return `${quantos} ${quantos === 1 ? singular : plural}`
}

/** O código recém-gerado, guardado com tudo o que a pessoa precisa para usá-lo. */
interface CodigoNaTela {
  /** De qual Campo ele é: é no cartão desse Campo que ele aparece. */
  campoId: string
  congregacao: string
  codigo: CodigoDoNotebook
}

type EmFoco =
  | { o: 'nada' }
  | { o: 'campo-novo' }
  /** `doTreinamento`: o código é para um notebook do treinamento, e a congregação é a do gêmeo. */
  | { o: 'codigo'; campo: CampoNaLista; doTreinamento?: boolean }
  | { o: 'apagar'; campo: CampoNaLista }
  | { o: 'desativar'; campo: CampoNaLista }
  | { o: 'recomecar'; campo: CampoNaLista }
  /** Um dos formulários do cadastro: corrigir, tirar, acrescentar ou renomear. */
  | { o: 'cadastro'; campo: CampoNaLista; qual: FocoDoCadastro }

export function OsCampos({
  eu,
  aoSair
}: {
  eu: EuDaAdministracao
  aoSair: () => void
}): JSX.Element {
  const [campos, setCampos] = useState<CampoNaLista[] | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [recado, setRecado] = useState<string | null>(null)
  /** O que deu certo pela metade: o Campo nasceu, e alguém ficou sem conta de painel. */
  const [aviso, setAviso] = useState<string | null>(null)
  const [foco, setFoco] = useState<EmFoco>({ o: 'nada' })
  const [codigo, setCodigo] = useState<CodigoNaTela | null>(null)
  /** Qual Campo está com uma ação em andamento. Evita dois cliques no mesmo botão. */
  const [ocupado, setOcupado] = useState<string | null>(null)
  /**
   * O ERRO DE UMA AÇÃO FICA NO CARTÃO DAQUELE CAMPO, e não no alto da página.
   *
   * No celular, a lista desce bem abaixo da primeira dobra: um aviso escrito no alto, com a pessoa
   * olhando para o cartão lá embaixo, é um aviso que ninguém lê. Foi o que aconteceu no ensaio de
   * 14/10/2026, com o código recém-gerado aparecendo fora da tela.
   */
  const [erroNoCartao, setErroNoCartao] = useState<{ campoId: string; frase: string } | null>(null)

  const carregar = useCallback(() => {
    setErro(null)
    administracao
      .campos()
      .then((r) => setCampos(r.campos))
      .catch((falha: unknown) => {
        if (ehSessaoTerminada(falha)) return aoSair()
        setErro(
          falha instanceof ErroDoServidor
            ? falha.message
            : 'Não foi possível carregar a lista de Campos.'
        )
      })
  }, [aoSair])

  useEffect(carregar, [carregar])

  /** Este servidor guarda os e-mails na caixa de saída, em vez de mandar? Só no da auditoria. */
  const [temCaixa, setTemCaixa] = useState(false)
  useEffect(() => {
    servidor.sobre().then((s) => setTemCaixa(s.emails_por === 'caixa_de_saida')).catch(() => {})
  }, [])

  function deuErro(campoId: string, falha: unknown, padrao: string): void {
    if (ehSessaoTerminada(falha)) return aoSair()
    setErroNoCartao({
      campoId,
      frase: falha instanceof ErroDoServidor ? falha.message : padrao
    })
  }

  async function criarOTreinamento(campo: CampoNaLista): Promise<void> {
    setErroNoCartao(null)
    setRecado(null)
    setOcupado(campo.id)
    try {
      const feito = await administracao.criarOTreinamento(campo.id)
      setRecado(`${feito.nome}: ${feito.recado}`)
      carregar()
    } catch (falha) {
      deuErro(campo.id, falha, 'Não foi possível criar o treinamento.')
    } finally {
      setOcupado(null)
    }
  }

  async function mandarOsLinks(campo: CampoNaLista): Promise<void> {
    setErroNoCartao(null)
    setRecado(null)
    setOcupado(campo.id)
    try {
      const feito = await administracao.mandarOsLinksDeNovo(campo.id)
      /*
       * O link anterior NÃO deixa de valer: ele vale até vencer, nas 48 horas dele. A frase dizia
       * o contrário até 15/10/2026, e quem lesse mandaria a pessoa jogar fora um link bom.
       */
      setRecado(
        feito.mandados === 0
          ? 'Ninguém deste Campo está esperando link: todos já criaram a senha.'
          : feito.mandados === 1
            ? 'Um link novo foi posto na fila. Ele vale 48 horas.'
            : `${feito.mandados} links novos foram postos na fila. Cada um vale 48 horas.`
      )
      carregar()
    } catch (falha) {
      deuErro(campo.id, falha, 'Não foi possível mandar os links.')
    } finally {
      setOcupado(null)
    }
  }

  const ativos = campos?.filter((c) => c.ativo).length ?? 0
  const comTreinamento = campos?.filter((c) => c.gemeo !== null).length ?? 0

  return (
    <>
      <BarraDaAdministracao eu={eu} aoSair={aoSair} />

      <main className="folha administracao" data-pronto={campos !== null ? 'sim' : erro !== null ? 'erro' : 'nao'}>
        <h1>Campos</h1>

        {temCaixa && (
          <p className="linha-form">
            <a className="botao-link" href="/administracao/caixa-de-saida">
              Caixa de saída: os e-mails deste ambiente
            </a>
          </p>
        )}

        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {campos === null && erro === null && <p className="recado aguarde">Carregando...</p>}
        {recado !== null && <p className="recado feito" role="status">{recado}</p>}
        {aviso !== null && <p className="recado atencao-amarela" role="alert">{aviso}</p>}

        {campos !== null && (
          <>
            <p className="totais">
              <span>{contagem(campos.length, 'Campo', 'Campos')}</span>
              <span>{ativos === campos.length ? 'todos ativos' : `${ativos} ativos`}</span>
              <span>
                {comTreinamento === 0
                  ? 'nenhum com treinamento'
                  : `${comTreinamento} com treinamento`}
              </span>
            </p>

            {foco.o !== 'campo-novo' && (
              <p className="linha-form">
                <button className="principal" onClick={() => { setFoco({ o: 'campo-novo' }); setCodigo(null) }}>
                  Criar um Campo novo
                </button>
              </p>
            )}

            {foco.o === 'campo-novo' && (
              <CampoNovo
                aoDesistir={() => setFoco({ o: 'nada' })}
                aoCriar={(feito) => {
                  setFoco({ o: 'nada' })
                  setAviso(feito.avisos.length > 0 ? feito.avisos.join(' ') : null)
                  setRecado(
                    feito.novo
                      ? `${feito.nome} foi criado, com ` +
                        `${contagem(feito.congregacoes.length, 'congregação', 'congregações')} e ` +
                        `${contagem(feito.pessoas.length, 'pessoa', 'pessoas')}. ` +
                        (feito.links_mandados > 0
                          ? feito.links_mandados === 1
                            ? '1 link de criar a senha foi posto na fila.'
                            : `${feito.links_mandados} links de criar a senha foram postos na fila.`
                          : 'Nenhum link foi mandado.')
                      : `${feito.nome} já existia, e nada foi criado de novo.`
                  )
                  carregar()
                }}
                aoSair={aoSair}
              />
            )}

            {campos.length === 0 && (
              <p className="recado nada">
                Nenhum Campo neste servidor ainda. O primeiro se cria no botão acima.
              </p>
            )}

            <ul className="campos-da-administracao">
              {campos.map((campo) => (
                <li key={campo.id} className={campo.ativo ? undefined : 'desativado'}>
                  <span className="nome">
                    {campo.nome}
                    {!campo.ativo && <span className="situacao NAO_FECHOU">desativado</span>}
                    {campo.pode_apagar && <span className="situacao TRAVADO">sem lançamento</span>}
                  </span>

                  <span className="detalhe">
                    {contagem(campo.congregacoes, 'congregação', 'congregações')}
                    {' · '}
                    {contagem(campo.pessoas, 'pessoa', 'pessoas')}
                    {' · '}
                    {contagem(campo.notebooks_ligados, 'notebook', 'notebooks')}
                    {campo.notebooks_cortados > 0 &&
                      ` (${contagem(campo.notebooks_cortados, 'cortado', 'cortados')})`}
                  </span>

                  <span className="detalhe">
                    {campo.ultimo_envio === null
                      ? 'nenhum notebook enviou ainda'
                      : `último envio ${quandoFoi(campo.ultimo_envio)}`}
                    {campo.ultimo_envio !== null && <> ({dia(campo.ultimo_envio)})</>}
                    {' · criado em '}{dia(campo.criado_em)}
                  </span>

                  {campo.ainda_nao_ativaram > 0 && (
                    <span className="detalhe atencao">
                      {campo.ainda_nao_ativaram === 1
                        ? '1 pessoa ainda não criou a senha dela pelo link'
                        : `${campo.ainda_nao_ativaram} pessoas ainda não criaram a senha delas pelo link`}
                    </span>
                  )}

                  {/*
                    QUEM É QUEM, com o e-mail de cada um e como foi o envio do link. Pedido do
                    ensaio de 15/10/2026: a contagem dizia QUANTOS, e a pergunta é QUEM.
                  */}
                  <OCadastro
                    campo={campo}
                    foco={foco.o === 'cadastro' && foco.campo.id === campo.id ? foco.qual : null}
                    aoFocar={(qual) => {
                      setFoco(qual === null ? { o: 'nada' } : { o: 'cadastro', campo, qual })
                    }}
                    aoMudar={carregar}
                    aoSair={aoSair}
                  />

                  {/*
                    A SUB-LINHA DO TREINAMENTO.

                    Recuada, com o nome do gêmeo e a geração. Sem gêmeo, ela é o convite para
                    criar: os Campos que já existem também ganham treinamento, e é por isso que
                    ele nasce pelo botão e não junto com o Campo.
                  */}
                  <span className="treinamento-do-campo">
                    {campo.gemeo === null ? (
                      <>
                        <span className="detalhe">Sem treinamento.</span>
                        <button
                          className="mini"
                          disabled={ocupado === campo.id}
                          onClick={() => void criarOTreinamento(campo)}
                        >
                          {ocupado === campo.id ? 'Criando...' : 'Criar o treinamento deste Campo'}
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="detalhe">
                          <strong>Treinamento:</strong> {campo.gemeo.nome}
                          {' · '}
                          {campo.gemeo.geracao === 0
                            ? 'como nasceu'
                            : `recomeçado ${contagem(campo.gemeo.geracao, 'vez', 'vezes')}`}
                          {' · '}
                          {contagem(campo.gemeo.notebooks.ligados, 'notebook de treino', 'notebooks de treino')}
                          {campo.gemeo.notebooks.atrasados > 0 && (
                            <>
                              {', '}
                              <span className="atencao">
                                {contagem(campo.gemeo.notebooks.atrasados, 'numa geração antiga', 'numa geração antiga')}
                              </span>
                            </>
                          )}
                        </span>
                        <button className="mini" onClick={() => setFoco({ o: 'recomecar', campo })}>
                          Recomeçar o treinamento
                        </button>
                        {campo.ativo && (
                          <button
                            className="mini"
                            onClick={() => { setFoco({ o: 'codigo', campo, doTreinamento: true }); setCodigo(null) }}
                          >
                            Código de um notebook do treinamento
                          </button>
                        )}
                      </>
                    )}
                  </span>

                  <span className="acoes so-tela">
                    {campo.ativo && (
                      <button className="mini" onClick={() => { setFoco({ o: 'codigo', campo }); setCodigo(null) }}>
                        Código de um notebook
                      </button>
                    )}
                    {campo.ainda_nao_ativaram > 0 && (
                      <button
                        className="mini"
                        disabled={ocupado === campo.id}
                        onClick={() => void mandarOsLinks(campo)}
                      >
                        Mandar os links de novo
                      </button>
                    )}
                    {campo.pode_apagar ? (
                      <button className="mini perigo" onClick={() => setFoco({ o: 'apagar', campo })}>
                        Apagar este Campo
                      </button>
                    ) : campo.ativo ? (
                      <button className="mini perigo" onClick={() => setFoco({ o: 'desativar', campo })}>
                        Desativar
                      </button>
                    ) : null}
                  </span>

                  {/*
                    O ERRO E O CÓDIGO FICAM AQUI, dentro do cartão do Campo a que pertencem.

                    No ensaio de 14/10/2026 o código gerado apareceu no alto da página, fora da
                    tela de quem estava olhando o cartão, e pareceu que a geração tinha falhado em
                    silêncio. Ela não tinha: o código nasceu, e ficou perdido.
                  */}
                  {erroNoCartao !== null && erroNoCartao.campoId === campo.id && (
                    <p className="recado" role="alert">{erroNoCartao.frase}</p>
                  )}

                  {codigo !== null && codigo.campoId === campo.id && (
                    <OCodigo oCodigo={codigo} aoFechar={() => setCodigo(null)} />
                  )}

                  {foco.o === 'codigo' && foco.campo.id === campo.id && (
                    <FormularioDoCodigo
                      campoId={foco.doTreinamento === true && campo.gemeo !== null ? campo.gemeo.id : campo.id}
                      doTreinamento={foco.doTreinamento === true}
                      aoDesistir={() => setFoco({ o: 'nada' })}
                      aoGerar={(gerado, congregacao) => {
                        setCodigo({
                          campoId: campo.id,
                          congregacao: foco.doTreinamento === true ? `${congregacao}, no treinamento` : congregacao,
                          codigo: gerado
                        })
                        setFoco({ o: 'nada' })
                      }}
                      aoSair={aoSair}
                    />
                  )}

                  {foco.o === 'recomecar' && foco.campo.id === campo.id && (
                    <FormularioComMotivo
                      titulo="Recomeçar o treinamento apaga o que foi lançado lá dentro, e isso vale para a equipe INTEIRA do Campo. O cadastro volta como ele está agora."
                      rotulo="Por que o treinamento está sendo recomeçado?"
                      exemplo="A turma de domingo vai começar de novo"
                      botao="Recomeçar o treinamento"
                      aoDesistir={() => setFoco({ o: 'nada' })}
                      fazer={async (motivo) => {
                        const feito = await administracao.recomecarOTreinamento(campo.id, motivo)
                        return (
                          `O treinamento de ${campo.nome} foi zerado, e o cadastro voltou em ` +
                          `${contagem(feito.linhas, 'linha', 'linhas')}. Nada da fila foi apagado.`
                        )
                      }}
                      aoFazer={(frase) => { setRecado(frase); setFoco({ o: 'nada' }); carregar() }}
                      aoSair={aoSair}
                    />
                  )}

                  {foco.o === 'desativar' && foco.campo.id === campo.id && (
                    <FormularioComMotivo
                      titulo="Desativar NÃO apaga nada. O Campo para de receber, os notebooks dele são cortados, e tudo o que já foi lançado continua lá."
                      rotulo="Por que este Campo está sendo desativado?"
                      exemplo="O Campo parou de usar o sistema neste mês"
                      botao="Desativar este Campo"
                      perigo
                      aoDesistir={() => setFoco({ o: 'nada' })}
                      fazer={async (motivo) => {
                        const feito = await administracao.desativar(campo.id, motivo)
                        return (
                          `${feito.nome} foi desativado, e ` +
                          `${contagem(
                            feito.notebooks_cortados,
                            'notebook foi cortado',
                            'notebooks foram cortados'
                          )}.`
                        )
                      }}
                      aoFazer={(frase) => { setRecado(frase); setFoco({ o: 'nada' }); carregar() }}
                      aoSair={aoSair}
                    />
                  )}

                  {foco.o === 'apagar' && foco.campo.id === campo.id && (
                    <FormularioComMotivo
                      titulo="Apagar serve para Campo criado por engano, e só funciona enquanto nenhum notebook lançou nada. A fila de eventos NÃO é apagada: o servidor desfaz pelo caminho inverso, e tudo fica registrado."
                      rotulo="Por que este Campo está sendo apagado?"
                      exemplo="Campo criado com o nome errado, hoje de manhã"
                      botao="Apagar este Campo"
                      perigo
                      pedirASenha
                      aoDesistir={() => setFoco({ o: 'nada' })}
                      fazer={async (motivo, senha) => {
                        const feito = await administracao.apagar(campo.id, motivo, senha)
                        return (
                          `${feito.nome} foi apagado. ` +
                          `${contagem(feito.notebooks_cortados, 'notebook cortado', 'notebooks cortados')}, ` +
                          `${contagem(feito.codigos_queimados, 'código queimado', 'códigos queimados')}, ` +
                          `${contagem(
                            feito.contas_do_painel_desativadas,
                            'conta de painel desativada',
                            'contas de painel desativadas'
                          )}.`
                        )
                      }}
                      aoFazer={(frase) => { setRecado(frase); setFoco({ o: 'nada' }); carregar() }}
                      aoSair={aoSair}
                    />
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </main>
    </>
  )
}

/**
 * O código de inscrição, mostrado UMA VEZ, **no cartão do Campo dele**.
 *
 * Ele fica na tela enquanto a pessoa não fechar, com três coisas que o ensaio de 14/10/2026 provou
 * que faltavam:
 *
 * 1. **de qual congregação ele é**, porque errar isso deixa o notebook falando pela congregação
 *    errada;
 * 2. **até quando ele vale**, na hora do relógio de quem está lendo. O servidor guarda em UTC, e
 *    quem lê a tela está em outro fuso;
 * 3. **o endereço deste servidor**, que é o que a pessoa vai digitar no notebook. **Foi esta linha
 *    que faltou**: o código era do ambiente de teste e o notebook, recém-zerado, falava com o
 *    endereço de fábrica, que é o de produção. O servidor de lá respondeu, com razão, que não
 *    conhecia aquele código.
 */
function OCodigo({
  oCodigo,
  aoFechar
}: {
  oCodigo: CodigoNaTela
  aoFechar: () => void
}): JSX.Element {
  const vence = new Date(oCodigo.codigo.expira_em)

  return (
    <section className="codigo-de-inscricao" role="status">
      <p className="titulo">Código para {oCodigo.congregacao}</p>
      <p className="codigo">{oCodigo.codigo.codigo}</p>

      <p className="explicacao">
        <strong>Digite este código no computador da congregação</strong>, na primeira abertura do
        programa, em "este computador vai entrar num Campo que já existe".
      </p>

      <p className="explicacao">
        Servidor: <strong>{window.location.host}</strong>
        <br />
        Vale até {vence.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
        {' '}({contagem(oCodigo.codigo.validade_em_horas, 'hora', 'horas')})
      </p>

      <p className="explicacao atencao">
        <strong>Confira o endereço do servidor no computador antes de digitar o código.</strong> Um
        código deste servidor não vale em nenhum outro, e lá ele aparece como código desconhecido.
      </p>

      {/*
        A frase de "uma vez só" é a do SERVIDOR, e não uma cópia dela escrita aqui. Ele já a
        escreve inteira, com o pedido de não mandar por e-mail. A tela acrescenta só o que ela
        sabe e ele não: que o resumo é tudo o que fica guardado.
      */}
      <p className="explicacao atencao">
        {oCodigo.codigo.recado} Anote agora: o servidor guarda só o resumo dele, e não tem como
        mostrar de novo.
      </p>

      <button onClick={aoFechar}>Já anotei</button>
    </section>
  )
}

/** De qual congregação é o notebook, e por quantas horas o código vale. */
function FormularioDoCodigo({
  campoId,
  doTreinamento,
  aoDesistir,
  aoGerar,
  aoSair
}: {
  /** O Campo do código: o de verdade, ou o gêmeo do treinamento. */
  campoId: string
  doTreinamento?: boolean
  aoDesistir: () => void
  /** O código e o NOME da congregação escolhida: o cartão mostra os dois. */
  aoGerar: (codigo: CodigoDoNotebook, congregacao: string) => void
  aoSair: () => void
}): JSX.Element {
  const [congregacoes, setCongregacoes] = useState<CongregacaoDoCampo[] | null>(null)
  const [congregacaoId, setCongregacaoId] = useState('')
  const [nome, setNome] = useState('')
  const [horas, setHoras] = useState(24)
  const [erro, setErro] = useState<string | null>(null)
  const [indo, setIndo] = useState(false)

  useEffect(() => {
    administracao
      .congregacoesDe(campoId)
      .then((r) => {
        setCongregacoes(r.congregacoes)
        setCongregacaoId(r.congregacoes[0]?.id ?? '')
      })
      .catch((falha: unknown) => {
        if (ehSessaoTerminada(falha)) return aoSair()
        setErro(
          falha instanceof ErroDoServidor
            ? falha.message
            : 'Não foi possível carregar as congregações deste Campo.'
        )
      })
  }, [campoId, aoSair])

  async function gerar(evento: FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)
    setIndo(true)

    try {
      const gerado = await administracao.codigoDoNotebook(campoId, {
        congregacao_id: congregacaoId,
        ...(nome.trim() === '' ? {} : { nome_do_notebook: nome.trim() }),
        validade_em_horas: horas
      })
      const escolhida = congregacoes?.find((c) => c.id === congregacaoId)
      aoGerar(gerado, escolhida?.nome ?? 'esta congregação')
    } catch (falha) {
      if (ehSessaoTerminada(falha)) return aoSair()
      setErro(
        falha instanceof ErroDoServidor ? falha.message : 'Não foi possível gerar o código.'
      )
      setIndo(false)
    }
  }

  return (
    <form className="confirmar-reabrir" onSubmit={(e) => void gerar(e)}>
      {doTreinamento === true && (
        <p className="recado atencao" role="note">
          Este código é do <strong>treinamento</strong>: ele inscreve o computador no Campo de mentira,
          onde a equipe erra à vontade. No notebook, ele é digitado em "Ir para o treinamento", e não na
          primeira abertura do programa de verdade.
        </p>
      )}
      <p className="explicacao">
        {doTreinamento === true
          ? 'O código vira a chave do treinamento daquele computador.'
          : 'O código é digitado no computador da congregação, na primeira abertura do programa, e vira a chave permanente dele.'}
        <strong> Escolher a congregação errada deixa o notebook isolado</strong>: ele passa a falar
        pela congregação errada e não recebe o que é dele.
      </p>

      {erro !== null && <p className="recado" role="alert">{erro}</p>}
      {congregacoes === null && erro === null && <p className="recado aguarde">Carregando...</p>}

      {congregacoes !== null && (
        <>
          <label>
            De qual congregação é o computador?
            <select
              className="campo"
              value={congregacaoId}
              onChange={(e) => setCongregacaoId(e.target.value)}
              required
            >
              {congregacoes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}{c.e_sede ? ' (sede)' : ''}
                </option>
              ))}
            </select>
          </label>

          <label>
            Nome do computador, para você reconhecer depois
            <input
              className="campo"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Notebook da secretaria"
              maxLength={120}
            />
          </label>

          <label>
            Quantas horas o código vale
            <select
              className="campo"
              value={horas}
              onChange={(e) => setHoras(Number(e.target.value))}
            >
              <option value={24}>24 horas</option>
              <option value={72}>3 dias</option>
              <option value={24 * 7}>7 dias, o máximo</option>
            </select>
            <span className="ajuda-do-codigo">
              Vale mais tempo para quem vai instalar o programa noutro dia. O que a folga aumenta
              é a janela em que um código vazado ainda serve, e por isso ela não é o padrão.
            </span>
          </label>

          <div className="linha-form">
            <button type="submit" disabled={indo || congregacaoId === ''}>
              {indo ? 'Gerando...' : 'Gerar o código'}
            </button>
            <button type="button" onClick={aoDesistir} disabled={indo}>
              Desistir
            </button>
          </div>
        </>
      )}
    </form>
  )
}

/**
 * O formulário das três ações que pedem motivo escrito: recomeçar, desativar e apagar.
 *
 * Um componente para os três porque o que muda entre eles é texto, e texto é parâmetro. O que
 * NÃO muda é o que importa: o motivo é obrigatório e vai para a trilha, e o botão fica apagado
 * até o motivo ter o tamanho que o servidor exige.
 *
 * **Só o apagar pede a senha.** É a única ação que desfaz coisa, e o que a senha protege é o dedo
 * errado numa tela aberta.
 */
function FormularioComMotivo({
  titulo,
  rotulo,
  exemplo,
  botao,
  perigo,
  pedirASenha,
  fazer,
  aoFazer,
  aoDesistir,
  aoSair
}: {
  titulo: string
  rotulo: string
  exemplo: string
  botao: string
  perigo?: boolean
  pedirASenha?: boolean
  fazer: (motivo: string, senha: string) => Promise<string>
  aoFazer: (frase: string) => void
  aoDesistir: () => void
  aoSair: () => void
}): JSX.Element {
  const [motivo, setMotivo] = useState('')
  const [senha, setSenha] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [indo, setIndo] = useState(false)

  async function enviar(evento: FormEvent): Promise<void> {
    evento.preventDefault()
    setErro(null)
    setIndo(true)

    try {
      aoFazer(await fazer(motivo.trim(), senha))
    } catch (falha) {
      if (ehSessaoTerminada(falha)) return aoSair()
      setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível fazer isso.')
      setIndo(false)
      setSenha('')
    }
  }

  return (
    <form className="confirmar-reabrir" onSubmit={(e) => void enviar(e)}>
      <p className="recado atencao" role="note">{titulo}</p>

      <label>
        {rotulo}
        <input
          className="campo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder={exemplo}
          maxLength={500}
          required
        />
        <span className="ajuda-do-codigo">
          O motivo fica guardado na trilha deste servidor, com a data e a hora.
        </span>
      </label>

      {pedirASenha === true && (
        <label>
          Sua senha da Administração
          <input
            className="campo"
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
      )}

      {erro !== null && <p className="recado" role="alert">{erro}</p>}

      <div className="linha-form">
        <button
          type="submit"
          className={perigo === true ? 'perigo' : undefined}
          disabled={
            indo ||
            motivo.trim().length < 10 ||
            (pedirASenha === true && senha.length === 0)
          }
        >
          {indo ? 'Fazendo...' : botao}
        </button>
        <button type="button" onClick={aoDesistir} disabled={indo}>
          Deixar como está
        </button>
      </div>
    </form>
  )
}
